/**
 * خدمات الدليل التشغيلي للمتجر وقسم «دور كل موظف»:
 * 1) تنظيم الدليل حسب البراندات والتشغيل المشترك
 * 2) أقسام فتح وإغلاق الفرع، المنتجات والصلاحية والتخزين، والجداول المتقدمة
 * 3) توثيق الوظائف والمهام والمسؤوليات لكل دور داخل المتجر
 * 4) عزل تام لبيانات كل منشأة مع تدقيق الصلاحيات والخادم
 */

import type { DataGateway } from '@/data/types';
import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import { deleteAttachment, uploadAttachment } from './attachment-service';
import type { Actor } from './org-service';

export const MANUAL_META_PREFIX = {
  item: 'tf-meta://manual-item',
  staffRole: 'tf-meta://staff-role',
  spreadsheet: 'tf-meta://manual-sheet',
} as const;

/* ============================================================================
 * نماذج الدليل التشغيلي (Manual Items)
 * ========================================================================== */

export type ManualItemCategory =
  | 'opening_closing' // فتح وإغلاق الفرع
  | 'products_storage' // المنتجات والصلاحية والتخزين
  | 'spreadsheet' // جداول ومستندات إكسيل و PDF
  | 'recipe' // وصفات وتحضير
  | 'product' // دليل المنتجات والمكونات
  | 'standard' // معايير الجودة والتشغيل
  | 'instruction' // تعليمات وإجراءات العمل
  | 'general'; // دليل عام

export const MANUAL_CATEGORY_LABELS: Record<ManualItemCategory, string> = {
  opening_closing: 'فتح وإغلاق الفرع',
  products_storage: 'المنتجات والصلاحية والتخزين',
  spreadsheet: 'جداول ومستندات إكسيل و PDF',
  recipe: 'وصفة وتحضير',
  product: 'منتج ومواصفات',
  standard: 'معايير جودة وتشغيل',
  instruction: 'إجراءات وتعليمات عمل',
  general: 'دليل تشغيلي عام',
};

/* ============================================================================
 * نماذج بنود فتح وإغلاق الفرع (Opening & Closing)
 * ========================================================================== */

export interface OpeningClosingChecklistItem {
  id: string;
  title: string;
  details?: string;
  timeWindow?: string; // مثلاً: قبل الفتح بـ 30 دقيقة
  isMandatory: boolean;
  roleTitle?: string; // مثلاً: كاشير، باريستا، مشرف فرع
}

export interface OpeningClosingData {
  openingItems: OpeningClosingChecklistItem[];
  closingItems: OpeningClosingChecklistItem[];
  generalNotes?: string;
}

/* ============================================================================
 * نماذج بيانات المنتجات ومدة الصلاحية وطرق التخزين
 * ========================================================================== */

export interface ProductStorageData {
  productName: string;
  sku?: string;
  category?: string;
  shelfLife: {
    value: number;
    unit: 'hours' | 'days' | 'months';
    condition?: string; // مثلاً: مجمد بدرجة -18 مئوية
  };
  openedShelfLife?: {
    value: number;
    unit: 'hours' | 'days';
    condition?: string; // مثلاً: بعد الفتح في الثلاجة
  };
  storageMethod: 'ambient' | 'refrigerated' | 'frozen' | 'dry' | 'custom';
  storageMethodLabel?: string;
  temperatureRange?: string; // مثلاً: 2 إلى 5 درجات مئوية
  storageInstructions: string;
  handlingAlerts?: string;
}

export interface ManualItemRecord {
  id: string;
  companyId: string;
  title: string;
  category: ManualItemCategory;
  /** shared = تشغيل مشترك لكل البراندات | brand = خاص ببراند محدد */
  scope: 'shared' | 'brand';
  brandId: string | null;
  /** إن كان مشتركًا، هل يقتصر على براندات محددة أم ينطبق على الجميع */
  applicableBrandIds?: string[] | null;
  summary: string;
  content: string;
  status: 'active' | 'archived';
  sortOrder: number;
  attachmentId: string | null;
  attachmentName: string | null;
  attachmentMimeType: string | null;
  extraData?: string | null;
  authorUserId: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateManualItemInput {
  title: string;
  category: ManualItemCategory;
  scope: 'shared' | 'brand';
  brandId?: string | null;
  applicableBrandIds?: string[] | null;
  summary?: string;
  content: string;
  status?: 'active' | 'archived';
  sortOrder?: number;
  extraData?: string | null;
  file?: File | null;
}

/* ============================================================================
 * نماذج قسم «دور كل موظف» (Staff Roles & Responsibilities)
 * ========================================================================== */

export interface StaffRoleRecord {
  id: string;
  companyId: string;
  title: string; // اسم الوظيفة (كاشير، مسؤول تحضير، مسؤول نظافة، مدير فرع...)
  responsibilities: string; // وصف المسؤوليات والمهام
  scope: 'shared' | 'brand';
  brandId: string | null;
  status: 'active' | 'disabled';
  sortOrder: number;
  authorUserId: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateStaffRoleInput {
  title: string;
  responsibilities: string;
  scope?: 'shared' | 'brand';
  brandId?: string | null;
  status?: 'active' | 'disabled';
  sortOrder?: number;
}

/* ============================================================================
 * التحقق من الصلاحيات والعزل (Permissions & Multi-Tenant Isolation)
 * ========================================================================== */

export async function assertCanManageManual(
  gateway: DataGateway,
  actor: Actor,
): Promise<void> {
  if (actor.isPlatformSupport) return;

  if (actor.membershipId) {
    const membership = await gateway
      .table('memberships')
      .get(actor.membershipId)
      .catch(() => null);

    if (membership && membership.companyId === actor.companyId && membership.isCompanyOwner) {
      return;
    }

    const membershipRoles = await gateway
      .table('membership_roles')
      .list({
        filter: { membershipId: { op: 'eq', value: actor.membershipId } },
      })
      .catch(() => []);

    const roleIds = membershipRoles.map((r) => r.roleId);
    if (roleIds.length > 0) {
      const perms = await gateway
        .table('role_permissions')
        .list({
          filter: { roleId: { op: 'in', value: roleIds } },
        })
        .catch(() => []);

      const allowedKeys = [
        'company.manage',
        'company.settings.manage',
        'brand.manage',
        'task.template.manage',
      ];
      if (perms.some((p) => allowedKeys.includes(p.permissionKey))) {
        return;
      }
    }
  }

  throw AppError.forbidden('ليست لديك صلاحية لإدارة محتوى الدليل التشغيلي أو وظائف الموظفين');
}

export function assertCanViewManual(actor: Actor, targetCompanyId: string): void {
  if (actor.companyId !== targetCompanyId && !actor.isPlatformSupport) {
    throw AppError.forbidden('لا يمكنك الوصول إلى محتوى منشأة أخرى');
  }
}

/* ============================================================================
 * إدارة عناصر الدليل التشغيلي (Manual Items CRUD)
 * ========================================================================== */

export async function listManualItems(
  gateway: DataGateway,
  companyId: string,
): Promise<ManualItemRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });

  const items: ManualItemRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(`${MANUAL_META_PREFIX.item}/`) || !row.checksum) {
      continue;
    }
    try {
      const parsed = JSON.parse(row.checksum) as Omit<
        ManualItemRecord,
        'id' | 'companyId' | 'createdAt'
      >;
      items.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل البيانات غير المطابقة
    }
  }

  return items.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export async function createManualItem(
  gateway: DataGateway,
  actor: Actor,
  input: CreateManualItemInput,
  authorName: string,
): Promise<ManualItemRecord> {
  await assertCanManageManual(gateway, actor);

  const title = input.title.trim();
  if (title.length < 2) {
    throw AppError.validation('عنوان المادة التشغيلية قصير جدًا');
  }

  let attachmentId: string | null = null;
  let attachmentName: string | null = null;
  let attachmentMimeType: string | null = null;

  if (input.file) {
    const uploaded = await uploadAttachment(gateway, actor, {
      companyId: actor.companyId,
      taskId: null,
      file: input.file,
      kind: input.file.type.startsWith('image/') ? 'image' : 'document',
      clientRef: `manual-doc:${newId()}`,
    });
    attachmentId = uploaded.id;
    attachmentName = uploaded.fileName;
    attachmentMimeType = uploaded.mimeType;
  }

  const id = newId();
  const now = new Date().toISOString();
  const record: Omit<ManualItemRecord, 'id' | 'companyId' | 'createdAt'> = {
    title,
    category: input.category,
    scope: input.scope,
    brandId: input.scope === 'brand' ? input.brandId ?? null : null,
    applicableBrandIds: input.scope === 'shared' ? input.applicableBrandIds ?? null : null,
    summary: input.summary?.trim() || '',
    content: input.content.trim(),
    status: input.status ?? 'active',
    sortOrder: input.sortOrder ?? 0,
    attachmentId,
    attachmentName,
    attachmentMimeType,
    extraData: input.extraData ?? null,
    authorUserId: actor.userId,
    authorName,
    updatedAt: now,
  };

  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${MANUAL_META_PREFIX.item}/${id}`,
    fileName: `manual-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(record),
    clientRef: `tf-manual:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...record,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function updateManualItem(
  gateway: DataGateway,
  actor: Actor,
  id: string,
  patch: Partial<CreateManualItemInput>,
): Promise<ManualItemRecord> {
  await assertCanManageManual(gateway, actor);

  const row = await gateway.table('attachments').get(id);
  if (!row || row.companyId !== actor.companyId) {
    throw AppError.notFound('المادة التشغيلية غير موجودة');
  }

  const existing = JSON.parse(row.checksum || '{}') as ManualItemRecord;
  const now = new Date().toISOString();

  let attachmentId = existing.attachmentId;
  let attachmentName = existing.attachmentName;
  let attachmentMimeType = existing.attachmentMimeType;

  if (patch.file) {
    if (existing.attachmentId) {
      await deleteAttachment(gateway, actor, existing.attachmentId).catch(() => undefined);
    }
    const uploaded = await uploadAttachment(gateway, actor, {
      companyId: actor.companyId,
      taskId: null,
      file: patch.file,
      kind: patch.file.type.startsWith('image/') ? 'image' : 'document',
      clientRef: `manual-doc:${newId()}`,
    });
    attachmentId = uploaded.id;
    attachmentName = uploaded.fileName;
    attachmentMimeType = uploaded.mimeType;
  }

  const updated: ManualItemRecord = {
    ...existing,
    id,
    companyId: row.companyId,
    title: patch.title !== undefined ? patch.title.trim() : existing.title,
    category: patch.category ?? existing.category,
    scope: patch.scope ?? existing.scope,
    brandId:
      patch.scope === 'shared'
        ? null
        : patch.brandId !== undefined
          ? patch.brandId
          : existing.brandId,
    applicableBrandIds:
      patch.scope === 'brand'
        ? null
        : patch.applicableBrandIds !== undefined
          ? patch.applicableBrandIds
          : existing.applicableBrandIds,
    summary: patch.summary !== undefined ? patch.summary.trim() : existing.summary,
    content: patch.content !== undefined ? patch.content.trim() : existing.content,
    status: patch.status ?? existing.status,
    sortOrder: patch.sortOrder ?? existing.sortOrder,
    attachmentId,
    attachmentName,
    attachmentMimeType,
    extraData: patch.extraData !== undefined ? patch.extraData : existing.extraData,
    updatedAt: now,
  };

  await gateway.table('attachments').update(id, {
    checksum: JSON.stringify(updated),
    updatedAt: now,
  });

  return updated;
}

export async function deleteManualItem(
  gateway: DataGateway,
  actor: Actor,
  id: string,
): Promise<void> {
  await assertCanManageManual(gateway, actor);

  const row = await gateway.table('attachments').get(id);
  if (!row || row.companyId !== actor.companyId) {
    throw AppError.notFound('المادة التشغيلية غير موجودة');
  }

  try {
    const parsed = JSON.parse(row.checksum || '{}') as ManualItemRecord;
    if (parsed.attachmentId) {
      await deleteAttachment(gateway, actor, parsed.attachmentId).catch(() => undefined);
    }
  } catch {
    // تجاهل
  }

  const now = new Date().toISOString();
  await gateway.table('attachments').update(id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

/* ============================================================================
 * إدارة قسم «دور كل موظف» (Staff Roles CRUD)
 * ========================================================================== */

export async function listStaffRoles(
  gateway: DataGateway,
  companyId: string,
): Promise<StaffRoleRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
  });

  const roles: StaffRoleRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(`${MANUAL_META_PREFIX.staffRole}/`) || !row.checksum) {
      continue;
    }
    try {
      const parsed = JSON.parse(row.checksum) as Omit<
        StaffRoleRecord,
        'id' | 'companyId' | 'createdAt'
      >;
      roles.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل
    }
  }

  return roles.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.title.localeCompare(b.title, 'ar');
  });
}

export async function createStaffRole(
  gateway: DataGateway,
  actor: Actor,
  input: CreateStaffRoleInput,
  authorName: string,
): Promise<StaffRoleRecord> {
  await assertCanManageManual(gateway, actor);

  const title = input.title.trim();
  if (title.length < 2) {
    throw AppError.validation('اسم الوظيفة قصير جدًا (حرفان على الأقل)');
  }
  const responsibilities = input.responsibilities.trim();
  if (responsibilities.length < 3) {
    throw AppError.validation('وصف المسؤوليات والمهام مطلوب');
  }

  const id = newId();
  const now = new Date().toISOString();
  const record: Omit<StaffRoleRecord, 'id' | 'companyId' | 'createdAt'> = {
    title,
    responsibilities,
    scope: input.scope ?? 'shared',
    brandId: input.scope === 'brand' ? input.brandId ?? null : null,
    status: input.status ?? 'active',
    sortOrder: input.sortOrder ?? 0,
    authorUserId: actor.userId,
    authorName,
    updatedAt: now,
    deletedAt: null,
  };

  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${MANUAL_META_PREFIX.staffRole}/${id}`,
    fileName: `role-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(record),
    clientRef: `tf-staffrole:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...record,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function updateStaffRole(
  gateway: DataGateway,
  actor: Actor,
  id: string,
  patch: Partial<CreateStaffRoleInput>,
): Promise<StaffRoleRecord> {
  await assertCanManageManual(gateway, actor);

  const row = await gateway.table('attachments').get(id);
  if (!row || row.companyId !== actor.companyId) {
    throw AppError.notFound('سجل الوظيفة غير موجود');
  }

  const existing = JSON.parse(row.checksum || '{}') as StaffRoleRecord;
  const now = new Date().toISOString();

  const title = patch.title !== undefined ? patch.title.trim() : existing.title;
  if (title.length < 2) {
    throw AppError.validation('اسم الوظيفة قصير جدًا');
  }
  const responsibilities =
    patch.responsibilities !== undefined
      ? patch.responsibilities.trim()
      : existing.responsibilities;
  if (responsibilities.length < 3) {
    throw AppError.validation('وصف المسؤوليات والمهام مطلوب');
  }

  const updated: StaffRoleRecord = {
    ...existing,
    id,
    companyId: row.companyId,
    title,
    responsibilities,
    scope: patch.scope ?? existing.scope,
    brandId:
      patch.scope === 'shared'
        ? null
        : patch.brandId !== undefined
          ? patch.brandId
          : existing.brandId,
    status: patch.status ?? existing.status,
    sortOrder: patch.sortOrder ?? existing.sortOrder,
    updatedAt: now,
  };

  await gateway.table('attachments').update(id, {
    checksum: JSON.stringify(updated),
    updatedAt: now,
  });

  return updated;
}

export async function toggleStaffRoleStatus(
  gateway: DataGateway,
  actor: Actor,
  id: string,
  active: boolean,
): Promise<StaffRoleRecord> {
  return updateStaffRole(gateway, actor, id, {
    status: active ? 'active' : 'disabled',
  });
}

export async function deleteStaffRole(
  gateway: DataGateway,
  actor: Actor,
  id: string,
): Promise<void> {
  await assertCanManageManual(gateway, actor);

  const row = await gateway.table('attachments').get(id);
  if (!row || row.companyId !== actor.companyId) {
    throw AppError.notFound('سجل الوظيفة غير موجود');
  }

  const now = new Date().toISOString();
  await gateway.table('attachments').update(id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

// إعادة تصدير خدمات محرر الجداول
export * from './spreadsheet-service';
